import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import MaskedInput from 'react-text-mask';
import { default as Selector } from 'react-select';
import {
  Button,
  Card,
  Col,
  Form,
  OverlayTrigger,
  Pagination,
  Row,
  Spinner,
  Tab,
  Table,
  Tabs,
  Tooltip,
} from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaExclamationCircle,
  FaFileExcel,
  FaSearch,
} from 'react-icons/fa';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Header from '~/Components/Header/Header';
import DateMask from '~/Components/DateMask';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as constants from '~/constant';
import * as api from '~/Services/Service';
import { formatarMonetario, salvarEmExcel } from '~/Utils/Helpers';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const REGISTROS_POR_PAGINA = 20;

const formatarData = (valor) => {
  if (!valor) return '';
  const data = moment(valor);
  return data.isValid() ? data.format('DD/MM/YYYY') : '';
};

const colunas = [
  { key: 'ge', label: 'GE', align: 'center', render: (item) => item.ge },
  {
    key: 'empreendimento',
    label: 'Empreendimento',
    align: 'left',
    render: (item) => item.empreendimento,
  },
  {
    key: 'data_rescisao',
    label: 'Data Rescisão',
    align: 'center',
    render: (item) => formatarData(item.data_rescisao),
  },
  {
    key: 'data_venda',
    label: 'Data Venda',
    align: 'center',
    render: (item) => formatarData(item.data_venda),
  },
  {
    key: 'codigo_boleto',
    label: 'Boleto',
    align: 'center',
    render: (item) => item.codigo_boleto,
  },
  {
    key: 'codigo_conta_cliente',
    label: 'Cliente',
    align: 'center',
    render: (item) => item.codigo_conta_cliente,
  },
  {
    key: 'nome_cliente',
    label: 'Nome',
    align: 'left',
    render: (item) => item.nome_cliente,
  },
  { key: 'quadra', label: 'Quadra', align: 'center', render: (item) => item.quadra },
  { key: 'lote', label: 'Lote', align: 'center', render: (item) => item.lote },
  {
    key: 'tipo_rescisao',
    label: 'Tipo Rescisão',
    align: 'center',
    render: (item) => item.tipo_rescisao,
  },
  {
    key: 'meses_duracao',
    label: 'Duração (meses)',
    align: 'center',
    render: (item) => item.meses_duracao,
  },
  {
    key: 'imobiliaria',
    label: 'Imobiliária',
    align: 'left',
    render: (item) => item.imobiliaria,
  },
  {
    key: 'corretor',
    label: 'Corretor',
    align: 'left',
    render: (item) => item.corretor,
  },
  {
    key: 'valor_venda',
    label: 'Valor Venda',
    align: 'right',
    render: (item) => formatarMonetario(item.valor_venda),
  },
  {
    key: 'tipo_sinal',
    label: 'Tipo Sinal',
    align: 'center',
    render: (item) => item.tipo_sinal,
  },
  {
    key: 'perc_pago_valor_venda',
    label: '% Pago VV',
    align: 'right',
    render: (item) =>
      item.perc_pago_valor_venda != null
        ? `${Number(item.perc_pago_valor_venda).toFixed(2)}%`
        : '',
  },
  {
    key: 'valor_quitacao',
    label: 'Valor Quitação',
    align: 'right',
    render: (item) => formatarMonetario(item.valor_quitacao),
  },
  {
    key: 'motivo',
    label: 'Motivo',
    align: 'left',
    render: (item) => item.motivo,
  },
  {
    key: 'sub_motivo',
    label: 'Sub-Motivo',
    align: 'left',
    render: (item) => item.sub_motivo,
  },
  {
    key: 'cobrador',
    label: 'Cobrador',
    align: 'left',
    render: (item) => item.cobrador,
  },
  {
    key: 'perfil_cliente',
    label: 'Perfil Cliente',
    align: 'left',
    render: (item) => item.perfil_cliente,
  },
  {
    key: 'cidade',
    label: 'Cidade',
    align: 'left',
    render: (item) => item.cidade,
  },
  {
    key: 'estado',
    label: 'UF',
    align: 'center',
    render: (item) => item.estado,
  },
];

export const RelatorioRescisoes = ({ embedded = false }) => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: 'data_rescisao',
    direction: 'desc',
  });
  const [openDatePickerInicial, setOpenDatePickerInicial] = useState(false);
  const [openDatePickerFinal, setOpenDatePickerFinal] = useState(false);

  const [mesTotalizadores, setMesTotalizadores] = useState(moment().month() + 1);
  const [anoTotalizadores, setAnoTotalizadores] = useState(moment().year());
  const [dadosTotalizadores, setDadosTotalizadores] = useState([]);
  const [resumoTotalizadores, setResumoTotalizadores] = useState([]);
  const [loadingTotalizadores, setLoadingTotalizadores] = useState(false);
  const [openPickerTotalizadores, setOpenPickerTotalizadores] = useState(false);

  const empreendimentos = useSelector((state) => state.empreendimentos);
  const gerentesCorretores = useSelector((state) => state.corretor);

  useEffect(() => {
    document.body.classList.add('page-relatorio-rescisoes');
    return () => document.body.classList.remove('page-relatorio-rescisoes');
  }, []);

  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const [larguraRolagem, setLarguraRolagem] = useState(0);

  const optionsEmpreendimentos = useMemo(() => {
    if (!empreendimentos || !empreendimentos.sort) return [];
    return empreendimentos.sort.map((item) => ({
      value: item.codigo,
      label: `${item.codigo} - ${item.descricao}`,
    }));
  }, [empreendimentos]);

  const optionsImobiliarias = useMemo(() => {
    if (
      !gerentesCorretores ||
      !gerentesCorretores.listaGerentes ||
      gerentesCorretores.listaGerentes.length === 0
    ) {
      return [];
    }
    return gerentesCorretores.listaGerentes.map((ger) => ({
      value: ger.codigo,
      label: ger.nome_imobiliaria,
    }));
  }, [gerentesCorretores]);

  const optionsCorretores = useMemo(() => {
    if (
      !gerentesCorretores ||
      !gerentesCorretores.listaCorretores ||
      gerentesCorretores.listaCorretores.length === 0
    ) {
      return [];
    }
    return gerentesCorretores.listaCorretores.map((cor) => ({
      value: cor.codigo,
      label: `${cor.codigo} - ${cor.nome}`,
    }));
  }, [gerentesCorretores]);

  const optionsEtapa = useMemo(
    () => [
      { value: 'sinal', label: 'Sinal' },
      { value: 'financiamento', label: 'Financiamento' },
    ],
    []
  );

  const dadosOrdenados = useMemo(() => {
    if (!sortConfig.key) return dados;
    const copia = [...dados];
    copia.sort((a, b) => {
      const valorA = a[sortConfig.key];
      const valorB = b[sortConfig.key];
      if (valorA == null && valorB == null) return 0;
      if (valorA == null) return 1;
      if (valorB == null) return -1;
      if (valorA < valorB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valorA > valorB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return copia;
  }, [dados, sortConfig]);

  const totalPaginas = Math.ceil(dadosOrdenados.length / REGISTROS_POR_PAGINA);

  const dadosPagina = useMemo(() => {
    const fim = paginaAtual * REGISTROS_POR_PAGINA;
    const inicio = fim - REGISTROS_POR_PAGINA;
    return dadosOrdenados.slice(inicio, fim);
  }, [dadosOrdenados, paginaAtual]);

  useEffect(() => {
    const bottom = bottomScrollRef.current;
    if (!bottom) return undefined;
    const atualizarLargura = () => setLarguraRolagem(bottom.scrollWidth);
    atualizarLargura();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(atualizarLargura);
      observer.observe(bottom);
      if (bottom.firstElementChild) observer.observe(bottom.firstElementChild);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', atualizarLargura);
    return () => window.removeEventListener('resize', atualizarLargura);
  }, [dadosPagina]);

  const sincronizandoRolagemRef = useRef(false);

  const sincronizarRolagem = (destinoRef) => (event) => {
    if (sincronizandoRolagemRef.current) {
      sincronizandoRolagemRef.current = false;
      return;
    }
    const destino = destinoRef.current;
    if (destino && destino.scrollLeft !== event.target.scrollLeft) {
      sincronizandoRolagemRef.current = true;
      destino.scrollLeft = event.target.scrollLeft;
    }
  };

  const totalRescindidas = useMemo(() => {
    const boletos = new Set();
    dadosTotalizadores.forEach((item) => boletos.add(item.codigo_boleto));
    return boletos.size;
  }, [dadosTotalizadores]);

  const totalEtapaSinal = useMemo(() => {
    const boletos = new Set();
    dadosTotalizadores.forEach((item) => {
      if (
        item.total_financiamento_pago == null ||
        Number(item.total_financiamento_pago) === 0
      ) {
        boletos.add(item.codigo_boleto);
      }
    });
    return boletos.size;
  }, [dadosTotalizadores]);

  const normalizarNome = (valor) =>
    (valor || '').trim().replace(/\s+/g, ' ');

  const top10Empreendimentos = useMemo(() => {
    const mapa = {};
    dadosTotalizadores.forEach((item) => {
      const original = normalizarNome(item.empreendimento) || '(sem empreendimento)';
      const chave = original.toUpperCase();
      if (!mapa[chave]) mapa[chave] = { nome: original, boletos: new Set() };
      mapa[chave].boletos.add(item.codigo_boleto);
    });
    return Object.keys(mapa)
      .map((chave) => ({ nome: mapa[chave].nome, total: mapa[chave].boletos.size }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [dadosTotalizadores]);

  const top10Corretores = useMemo(() => {
    const mapa = {};
    dadosTotalizadores.forEach((item) => {
      const original = normalizarNome(item.corretor) || '(sem corretor)';
      const chave = original.toUpperCase();
      if (!mapa[chave]) mapa[chave] = { nome: original, boletos: new Set() };
      mapa[chave].boletos.add(item.codigo_boleto);
    });
    return Object.keys(mapa)
      .map((chave) => ({ nome: mapa[chave].nome, total: mapa[chave].boletos.size }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [dadosTotalizadores]);

  const proporcaoCorretores = useMemo(() => {
    const rescindidasPorCorretor = {};
    dadosTotalizadores.forEach((item) => {
      const original = normalizarNome(item.corretor) || '(sem corretor)';
      const chave = original.toUpperCase();
      if (!rescindidasPorCorretor[chave]) {
        rescindidasPorCorretor[chave] = new Set();
      }
      rescindidasPorCorretor[chave].add(item.codigo_boleto);
    });

    const vendasPorCorretor = {};
    resumoTotalizadores.forEach((r) => {
      const original = normalizarNome(r.corretor);
      if (!original) return;
      const chave = original.toUpperCase();
      if (!vendasPorCorretor[chave]) {
        vendasPorCorretor[chave] = { nome: original, vendas_totais: 0 };
      }
      vendasPorCorretor[chave].vendas_totais += Number(r.vendas_totais) || 0;
    });

    return Object.keys(vendasPorCorretor)
      .filter((chave) => vendasPorCorretor[chave].vendas_totais > 0)
      .map((chave) => {
        const v = vendasPorCorretor[chave];
        const rescindidas = rescindidasPorCorretor[chave]
          ? rescindidasPorCorretor[chave].size
          : 0;
        const percentual = (rescindidas / v.vendas_totais) * 100;
        return {
          nome: v.nome,
          rescindidas,
          vendas_totais: v.vendas_totais,
          percentual,
        };
      })
      .filter((p) => p.rescindidas > 0)
      .sort((a, b) => b.percentual - a.percentual);
  }, [dadosTotalizadores, resumoTotalizadores]);

  const alturaGraficoProporcao = useMemo(
    () => Math.max(400, proporcaoCorretores.length * 28),
    [proporcaoCorretores]
  );

  const graficoTop10Empreendimentos = useMemo(
    () => ({
      labels: top10Empreendimentos.map((e) => e.nome),
      datasets: [
        {
          label: 'Rescisões',
          data: top10Empreendimentos.map((e) => e.total),
          backgroundColor: '#E67E22',
        },
      ],
    }),
    [top10Empreendimentos]
  );

  const graficoTop10Corretores = useMemo(
    () => ({
      labels: top10Corretores.map((c) => c.nome),
      datasets: [
        {
          label: 'Rescisões',
          data: top10Corretores.map((c) => c.total),
          backgroundColor: '#2980B9',
        },
      ],
    }),
    [top10Corretores]
  );

  const graficoProporcaoCorretores = useMemo(
    () => ({
      labels: proporcaoCorretores.map((p) => p.nome),
      datasets: [
        {
          label: '% Rescisão / Vendas',
          data: proporcaoCorretores.map((p) => Number(p.percentual.toFixed(2))),
          backgroundColor: '#C0392B',
        },
      ],
    }),
    [proporcaoCorretores]
  );

  const truncarRotulo = (texto) => {
    if (!texto) return '';
    return texto.length > 22 ? `${texto.substring(0, 22)}…` : texto;
  };

  const opcoesBarraHorizontal = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 12 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.parsed.x} rescisão(ões)`,
          },
        },
      },
      scales: {
        x: { beginAtZero: true, ticks: { precision: 0 } },
        y: {
          ticks: {
            font: { size: 11 },
            autoSkip: false,
            callback(value) {
              return truncarRotulo(this.getLabelForValue(value));
            },
          },
        },
      },
    }),
    []
  );

  const opcoesGraficoProporcao = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 12 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (ctxs) => {
              if (!ctxs || ctxs.length === 0) return '';
              const item = proporcaoCorretores[ctxs[0].dataIndex];
              return item ? item.nome : ctxs[0].label;
            },
            label: (ctx) => {
              const item = proporcaoCorretores[ctx.dataIndex];
              if (!item) return `${ctx.parsed.x}%`;
              return `${item.percentual.toFixed(2)}% (${item.rescindidas}/${item.vendas_totais})`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: { callback: (value) => `${value}%` },
        },
        y: {
          ticks: {
            font: { size: 11 },
            autoSkip: false,
            callback(value) {
              return truncarRotulo(this.getLabelForValue(value));
            },
          },
        },
      },
    }),
    [proporcaoCorretores]
  );

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const validationSchema = Yup.object().shape({
    data_inicial: Yup.string()
      .required('Data inicial obrigatória')
      .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato de data inválido')
      .test('valid-date', 'Data inválida', (value) =>
        value ? moment(value, 'DD/MM/YYYY', true).isValid() : false
      ),
    data_final: Yup.string()
      .required('Data final obrigatória')
      .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato de data inválido')
      .test('valid-date', 'Data inválida', (value) =>
        value ? moment(value, 'DD/MM/YYYY', true).isValid() : false
      )
      .test(
        'data-final-maior',
        'Data final não pode ser anterior à inicial',
        function (value) {
          const { data_inicial } = this.parent;
          if (!data_inicial || !value) return true;
          const inicial = moment(data_inicial, 'DD/MM/YYYY', true);
          const final = moment(value, 'DD/MM/YYYY', true);
          if (!inicial.isValid() || !final.isValid()) return true;
          return !final.isBefore(inicial);
        }
      ),
  });

  const montarFiltroApi = (valores) => ({
    data_inicial: moment(valores.data_inicial, 'DD/MM/YYYY').toDate(),
    data_final: moment(valores.data_final, 'DD/MM/YYYY').toDate(),
    empreendimentos: valores.empreendimentos || null,
    imobiliarias: valores.imobiliarias || null,
    corretores: valores.corretores || null,
    etapa: valores.etapa || null,
  });

  const buscarRelatorio = async (valores) => {
    try {
      setLoading(true);
      const resposta = await api.consultarRelatorioRescisoes(
        montarFiltroApi(valores)
      );
      const lista = resposta && resposta.dados ? resposta.dados : [];
      setDados(lista);
      setPaginaAtual(1);
      if (lista.length === 0) {
        toast.info('Nenhum registro encontrado para os filtros selecionados.');
      }
    } catch (err) {
      const mensagem =
        err && err.message ? err.message : 'Erro ao buscar relatório';
      toast.error(mensagem);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelado = false;
    const inicio = moment({
      year: anoTotalizadores,
      month: mesTotalizadores - 1,
      day: 1,
    })
      .startOf('day')
      .toDate();
    const fim = moment({
      year: anoTotalizadores,
      month: mesTotalizadores - 1,
      day: 1,
    })
      .endOf('month')
      .toDate();
    setLoadingTotalizadores(true);
    api
      .consultarRelatorioRescisoes({
        data_inicial: inicio,
        data_final: fim,
        empreendimentos: null,
      })
      .then((resposta) => {
        if (cancelado) return;
        setDadosTotalizadores(
          resposta && resposta.dados ? resposta.dados : []
        );
        setResumoTotalizadores(
          resposta && resposta.resumo_corretores
            ? resposta.resumo_corretores
            : []
        );
      })
      .catch((err) => {
        if (cancelado) return;
        const mensagem =
          err && err.message
            ? err.message
            : 'Erro ao carregar totalizadores';
        toast.error(mensagem);
        setDadosTotalizadores([]);
        setResumoTotalizadores([]);
      })
      .finally(() => {
        if (cancelado) return;
        setLoadingTotalizadores(false);
      });
    return () => {
      cancelado = true;
    };
  }, [mesTotalizadores, anoTotalizadores]);

  const exportarExcel = async () => {
    if (dados.length === 0) return;
    try {
      setExportandoExcel(true);
      await salvarEmExcel(dados, '/exportador/excel/relatorio-rescisoes');
      toast.success('Relatório exportado com sucesso!');
    } catch (err) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setExportandoExcel(false);
    }
  };

  const paginasExibir = useMemo(() => {
    if (totalPaginas <= 5) {
      return Array.from({ length: totalPaginas }, (_, i) => i + 1);
    }
    const paginas = [];
    const inicio = Math.max(1, paginaAtual - 2);
    const fim = Math.min(totalPaginas, inicio + 4);
    const inicioFinal = Math.max(1, fim - 4);
    for (let i = inicioFinal; i <= fim; i += 1) {
      paginas.push(i);
    }
    return paginas;
  }, [totalPaginas, paginaAtual]);

  return (
    <div className="relatorio-rescisoes-container">
      {!embedded && <Header title="Relatório de Rescisões" />}
      <div className="container-fluid pt-3 px-3 pb-4 rescisoes-tabs-wrapper">
        <Tabs
          defaultActiveKey="relatorio"
          id="tabs-relatorio-rescisoes"
          className="mb-0 rescisoes-tabs"
        >
          <Tab eventKey="relatorio" title="Relatório">
            <div className="pt-3">
      <Formik
        initialValues={{
          data_inicial: moment()
            .subtract(1, 'month')
            .startOf('month')
            .format('DD/MM/YYYY'),
          data_final: moment().format('DD/MM/YYYY'),
          empreendimentos: '',
          imobiliarias: '',
          corretores: '',
          etapa: '',
        }}
        validationSchema={validationSchema}
        onSubmit={buscarRelatorio}
      >
        {({
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          errors,
          touched,
          values,
        }) => (
          <Card className="shadow-sm border-0 mb-3 rescisoes-card">
            <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col sm={12} md={3}>
                <Form.Group>
                  <Form.Label>Data Inicial</Form.Label>
                  <DateMask allowEmpty>
                    {() => (
                      <div className="position-relative">
                        <MaskedInput
                          name="data_inicial"
                          type="tel"
                          guide={false}
                          className={`form-control ${
                            errors.data_inicial && touched.data_inicial
                              ? 'is-invalid'
                              : ''
                          }`}
                          placeholder="Ex.: 01/01/2026"
                          mask={constants.MASK_DATE}
                          value={values.data_inicial}
                          onChange={(e) =>
                            setFieldValue('data_inicial', e.target.value)
                          }
                          onBlur={() => setFieldTouched('data_inicial', true)}
                          style={{ paddingRight: '65px' }}
                        />
                        <div
                          className="position-absolute d-flex align-items-center"
                          style={{
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            gap: '5px',
                          }}
                        >
                          {errors.data_inicial && touched.data_inicial && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="tt-data-inicial-res">
                                  {errors.data_inicial}
                                </Tooltip>
                              }
                            >
                              <FaExclamationCircle
                                className="text-danger"
                                style={{ cursor: 'pointer' }}
                              />
                            </OverlayTrigger>
                          )}
                          <FaCalendarAlt
                            className="text-secondary"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setOpenDatePickerInicial(true)}
                          />
                        </div>
                        <MuiPickersUtilsProvider
                          utils={MomentUtils}
                          locale="pt-br"
                        >
                          <DatePicker
                            open={openDatePickerInicial}
                            onOpen={() => setOpenDatePickerInicial(true)}
                            onClose={() => setOpenDatePickerInicial(false)}
                            value={
                              values.data_inicial
                                ? moment(values.data_inicial, 'DD/MM/YYYY')
                                : null
                            }
                            onChange={(date) => {
                              setFieldValue(
                                'data_inicial',
                                date ? date.format('DD/MM/YYYY') : ''
                              );
                              setOpenDatePickerInicial(false);
                            }}
                            format="DD/MM/YYYY"
                            TextFieldComponent={() => null}
                          />
                        </MuiPickersUtilsProvider>
                      </div>
                    )}
                  </DateMask>
                </Form.Group>
              </Col>
              <Col sm={12} md={3}>
                <Form.Group>
                  <Form.Label>Data Final</Form.Label>
                  <DateMask allowEmpty>
                    {() => (
                      <div className="position-relative">
                        <MaskedInput
                          name="data_final"
                          type="tel"
                          guide={false}
                          className={`form-control ${
                            errors.data_final && touched.data_final
                              ? 'is-invalid'
                              : ''
                          }`}
                          placeholder="Ex.: 31/01/2026"
                          mask={constants.MASK_DATE}
                          value={values.data_final}
                          onChange={(e) =>
                            setFieldValue('data_final', e.target.value)
                          }
                          onBlur={() => setFieldTouched('data_final', true)}
                          style={{ paddingRight: '65px' }}
                        />
                        <div
                          className="position-absolute d-flex align-items-center"
                          style={{
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            gap: '5px',
                          }}
                        >
                          {errors.data_final && touched.data_final && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="tt-data-final-res">
                                  {errors.data_final}
                                </Tooltip>
                              }
                            >
                              <FaExclamationCircle
                                className="text-danger"
                                style={{ cursor: 'pointer' }}
                              />
                            </OverlayTrigger>
                          )}
                          <FaCalendarAlt
                            className="text-secondary"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setOpenDatePickerFinal(true)}
                          />
                        </div>
                        <MuiPickersUtilsProvider
                          utils={MomentUtils}
                          locale="pt-br"
                        >
                          <DatePicker
                            open={openDatePickerFinal}
                            onOpen={() => setOpenDatePickerFinal(true)}
                            onClose={() => setOpenDatePickerFinal(false)}
                            value={
                              values.data_final
                                ? moment(values.data_final, 'DD/MM/YYYY')
                                : null
                            }
                            onChange={(date) => {
                              setFieldValue(
                                'data_final',
                                date ? date.format('DD/MM/YYYY') : ''
                              );
                              setOpenDatePickerFinal(false);
                            }}
                            format="DD/MM/YYYY"
                            TextFieldComponent={() => null}
                          />
                        </MuiPickersUtilsProvider>
                      </div>
                    )}
                  </DateMask>
                </Form.Group>
              </Col>
              <Col sm={12} md={6}>
                <Form.Group>
                  <Form.Label>Empreendimentos</Form.Label>
                  <Selector
                    isMulti
                    isLoading={optionsEmpreendimentos.length === 0}
                    isDisabled={optionsEmpreendimentos.length === 0}
                    onChange={(selecionados) => {
                      setFieldValue(
                        'empreendimentos',
                        selecionados
                          ? selecionados.map((o) => o.value).join(',')
                          : ''
                      );
                    }}
                    options={optionsEmpreendimentos}
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    placeholder="Todos..."
                    isClearable
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col sm={12} md={4}>
                <Form.Group>
                  <Form.Label>Imobiliárias</Form.Label>
                  <Selector
                    isMulti
                    isLoading={optionsImobiliarias.length === 0}
                    isDisabled={optionsImobiliarias.length === 0}
                    onChange={(selecionados) => {
                      setFieldValue(
                        'imobiliarias',
                        selecionados
                          ? selecionados.map((o) => o.value).join(',')
                          : ''
                      );
                    }}
                    options={optionsImobiliarias}
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    placeholder="Todas..."
                    isClearable
                  />
                </Form.Group>
              </Col>
              <Col sm={12} md={4}>
                <Form.Group>
                  <Form.Label>Corretores</Form.Label>
                  <Selector
                    isMulti
                    isLoading={optionsCorretores.length === 0}
                    isDisabled={optionsCorretores.length === 0}
                    onChange={(selecionados) => {
                      setFieldValue(
                        'corretores',
                        selecionados
                          ? selecionados.map((o) => o.value).join(',')
                          : ''
                      );
                    }}
                    options={optionsCorretores}
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    placeholder="Todos..."
                    isClearable
                  />
                </Form.Group>
              </Col>
              <Col sm={12} md={2}>
                <Form.Group>
                  <Form.Label>Etapa</Form.Label>
                  <Selector
                    isLoading={false}
                    onChange={(selecionado) => {
                      setFieldValue(
                        'etapa',
                        selecionado ? selecionado.value : ''
                      );
                    }}
                    options={optionsEtapa}
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    placeholder="Todas..."
                    isClearable
                  />
                </Form.Group>
              </Col>
              <Col
                sm={12}
                md={2}
                className="d-flex align-items-end justify-content-end"
              >
                <Form.Group>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={
                      loading ||
                      !values.data_inicial ||
                      !values.data_final ||
                      (errors.data_inicial && touched.data_inicial) ||
                      (errors.data_final && touched.data_final)
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="mr-2"
                        />
                        Buscando...
                      </>
                    ) : (
                      <>
                        <FaSearch className="mr-2" />
                        Buscar
                      </>
                    )}
                  </Button>
                </Form.Group>
              </Col>
            </Row>
          </Form>
            </Card.Body>
          </Card>
        )}
      </Formik>

      {dados.length > 0 ? (
        <Card className="shadow-sm border-0 rescisoes-card">
          <Card.Body>
            <Row className="d-flex justify-content-between align-items-center mb-3">
              <Col>
                <span className="text-muted">
                  Total de <strong className="text-dark">{dados.length}</strong> registro(s) encontrado(s).
                </span>
              </Col>
              <Col xs="auto">
                <Button
                  variant="success"
                  onClick={exportarExcel}
                  disabled={exportandoExcel || loading}
                >
                  {exportandoExcel ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="mr-2"
                      />
                      Exportando...
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="mr-2" />
                      Exportar Excel
                    </>
                  )}
                </Button>
              </Col>
            </Row>

            <div
              ref={topScrollRef}
              className="relatorio-rescisoes-scroll-top"
              onScroll={sincronizarRolagem(bottomScrollRef)}
            >
              <div style={{ width: larguraRolagem, height: 1 }} />
            </div>
            <div
              ref={bottomScrollRef}
              className="relatorio-rescisoes-desktop"
              onScroll={sincronizarRolagem(topScrollRef)}
            >
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr className="text-center">
                    {colunas.map((coluna) => (
                      <th
                        key={coluna.key}
                        onClick={() => handleSort(coluna.key)}
                        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        {coluna.label}
                        {sortConfig.key === coluna.key
                          ? sortConfig.direction === 'asc'
                            ? ' ▲'
                            : ' ▼'
                          : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dadosPagina.map((item) => (
                    <tr
                      key={`${item.codigo_boleto}-${item.ge}-${item.quadra}-${item.lote}`}
                    >
                      {colunas.map((coluna) => (
                        <td
                          key={coluna.key}
                          className={`text-${coluna.align}`}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {coluna.render(item)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <Row className="d-flex justify-content-between align-items-center mt-3">
              <Col xs="auto">
                <small className="text-muted">
                  Mostrando{' '}
                  {(paginaAtual - 1) * REGISTROS_POR_PAGINA + 1} até{' '}
                  {Math.min(
                    paginaAtual * REGISTROS_POR_PAGINA,
                    dadosOrdenados.length
                  )}{' '}
                  de {dadosOrdenados.length} registros
                </small>
              </Col>
              <Col xs="auto">
                {totalPaginas > 1 && (
                  <Pagination className="mb-0">
                    <Pagination.First
                      onClick={() => setPaginaAtual(1)}
                      disabled={paginaAtual === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setPaginaAtual(paginaAtual - 1)}
                      disabled={paginaAtual === 1}
                    />
                    {paginasExibir.map((numero) => (
                      <Pagination.Item
                        key={numero}
                        active={numero === paginaAtual}
                        onClick={() => setPaginaAtual(numero)}
                      >
                        {numero}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => setPaginaAtual(paginaAtual + 1)}
                      disabled={paginaAtual === totalPaginas}
                    />
                    <Pagination.Last
                      onClick={() => setPaginaAtual(totalPaginas)}
                      disabled={paginaAtual === totalPaginas}
                    />
                  </Pagination>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ) : (
        !loading && (
          <Card className="shadow-sm border-0 rescisoes-card">
            <Card.Body className="text-center p-5">
              <FaExclamationCircle size={48} className="text-muted mb-3" />
              <h5 className="text-muted">Nenhum registro encontrado</h5>
              <p className="text-muted mb-0">
                Utilize os filtros acima para buscar as rescisões do período.
              </p>
            </Card.Body>
          </Card>
        )
      )}
            </div>
          </Tab>
          <Tab eventKey="graficos" title="Gráficos">
            <div className="pt-3">
              <Card className="shadow-sm border-0 mb-3 rescisoes-card">
                <Card.Body className="py-2">
                  <Row className="align-items-center">
                    <Col>
                      <small className="text-muted text-uppercase">
                        Período dos indicadores
                      </small>
                    </Col>
                    <Col xs="auto" className="d-flex align-items-center">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setOpenPickerTotalizadores(true)}
                        style={{ minWidth: 170, textTransform: 'capitalize' }}
                      >
                        <FaCalendarAlt className="mr-2" />
                        {moment({
                          year: anoTotalizadores,
                          month: mesTotalizadores - 1,
                          day: 1,
                        }).format('MMMM [de] YYYY')}
                      </Button>
                      <MuiPickersUtilsProvider utils={MomentUtils} locale="pt-br">
                        <DatePicker
                          open={openPickerTotalizadores}
                          onOpen={() => setOpenPickerTotalizadores(true)}
                          onClose={() => setOpenPickerTotalizadores(false)}
                          views={['year', 'month']}
                          openTo="month"
                          value={moment({
                            year: anoTotalizadores,
                            month: mesTotalizadores - 1,
                            day: 1,
                          })}
                          onChange={(date) => {
                            if (date) {
                              setAnoTotalizadores(date.year());
                              setMesTotalizadores(date.month() + 1);
                            }
                            setOpenPickerTotalizadores(false);
                          }}
                          format="MMMM [de] YYYY"
                          TextFieldComponent={() => null}
                        />
                      </MuiPickersUtilsProvider>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Row>
                <Col sm={6} className="mb-3">
                  <Card className="shadow-sm h-100 border-0 rescisoes-card rescisoes-kpi-danger">
                    <Card.Body className="d-flex flex-column align-items-center justify-content-center py-4">
                      <small className="text-muted text-uppercase mb-2">
                        Total de Rescisões
                      </small>
                      {loadingTotalizadores ? (
                        <Spinner
                          animation="border"
                          role="status"
                          variant="danger"
                        />
                      ) : (
                        <h2 className="mb-0 text-danger font-weight-bold">
                          {totalRescindidas}
                        </h2>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6} className="mb-3">
                  <Card className="shadow-sm h-100 border-0 rescisoes-card rescisoes-kpi-warning">
                    <Card.Body className="d-flex flex-column align-items-center justify-content-center py-4">
                      <small className="text-muted text-uppercase mb-2">
                        Rescisões na Etapa Sinal
                      </small>
                      {loadingTotalizadores ? (
                        <Spinner
                          animation="border"
                          role="status"
                          variant="warning"
                        />
                      ) : (
                        <h2 className="mb-0 text-warning font-weight-bold">
                          {totalEtapaSinal}
                        </h2>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col md={6} className="mb-3">
                  <Card className="shadow-sm h-100 border-0 rescisoes-card">
                    <Card.Header className="bg-white font-weight-bold rescisoes-card-header">
                      Top 10 Empreendimentos com Rescisões
                    </Card.Header>
                    <Card.Body>
                      {loadingTotalizadores ? (
                        <div
                          style={{ height: '350px' }}
                          className="d-flex align-items-center justify-content-center"
                        >
                          <Spinner animation="border" role="status" />
                        </div>
                      ) : top10Empreendimentos.length > 0 ? (
                        <div style={{ height: '350px' }}>
                          <Bar
                            data={graficoTop10Empreendimentos}
                            options={opcoesBarraHorizontal}
                          />
                        </div>
                      ) : (
                        <p className="text-muted text-center mb-0">
                          Sem dados para exibir.
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} className="mb-3">
                  <Card className="shadow-sm h-100 border-0 rescisoes-card">
                    <Card.Header className="bg-white font-weight-bold rescisoes-card-header">
                      Top 10 Corretores com Rescisões
                    </Card.Header>
                    <Card.Body>
                      {loadingTotalizadores ? (
                        <div
                          style={{ height: '350px' }}
                          className="d-flex align-items-center justify-content-center"
                        >
                          <Spinner animation="border" role="status" />
                        </div>
                      ) : top10Corretores.length > 0 ? (
                        <div style={{ height: '350px' }}>
                          <Bar
                            data={graficoTop10Corretores}
                            options={opcoesBarraHorizontal}
                          />
                        </div>
                      ) : (
                        <p className="text-muted text-center mb-0">
                          Sem dados para exibir.
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mb-3">
                  <Card className="shadow-sm border-0 rescisoes-card">
                    <Card.Header className="bg-white font-weight-bold rescisoes-card-header">
                      Proporção Rescisão × Vendas por Corretor (% Rescisões /
                      Vendas)
                    </Card.Header>
                    <Card.Body>
                      {loadingTotalizadores ? (
                        <div
                          style={{ height: '400px' }}
                          className="d-flex align-items-center justify-content-center"
                        >
                          <Spinner animation="border" role="status" />
                        </div>
                      ) : proporcaoCorretores.length > 0 ? (
                        <div
                          style={{ height: `${alturaGraficoProporcao}px` }}
                        >
                          <Bar
                            data={graficoProporcaoCorretores}
                            options={opcoesGraficoProporcao}
                          />
                        </div>
                      ) : (
                        <p className="text-muted text-center mb-0">
                          Sem dados para exibir.
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </Tab>
        </Tabs>
      </div>

      <style>{`
        body.page-relatorio-rescisoes .App { min-width: 0; }
        body.page-relatorio-rescisoes .ml-sm-auto { min-width: 0; background-color: #f4f6f9; }
        body.page-relatorio-rescisoes .relatorio-rescisoes-container,
        body.page-relatorio-rescisoes .relatorio-rescisoes-container .row,
        body.page-relatorio-rescisoes .relatorio-rescisoes-container .col,
        body.page-relatorio-rescisoes .relatorio-rescisoes-container [class^="col-"],
        body.page-relatorio-rescisoes .relatorio-rescisoes-container [class*=" col-"] { min-width: 0; }
        body.page-relatorio-rescisoes .relatorio-rescisoes-container .container-fluid { min-width: 0; max-width: 100%; }
        .relatorio-rescisoes-container {
          background-color: #f4f6f9;
          min-height: calc(100vh - 60px);
        }
        .relatorio-rescisoes-desktop {
          display: block;
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .relatorio-rescisoes-desktop > .table { margin-bottom: 0; white-space: nowrap; }
        .relatorio-rescisoes-scroll-top {
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
        }
        .rescisoes-card {
          border-radius: 8px;
          background-color: #ffffff;
        }
        .rescisoes-card-header {
          border-bottom: 1px solid #edf0f5;
          padding: 14px 20px;
          font-size: 14px;
          color: #495057;
          letter-spacing: 0.2px;
          border-radius: 8px 8px 0 0;
        }
        .rescisoes-kpi-danger {
          border-left: 4px solid #dc3545 !important;
        }
        .rescisoes-kpi-warning {
          border-left: 4px solid #ffc107 !important;
        }
        .rescisoes-tabs-wrapper {
          background-color: #f4f6f9;
        }
        .rescisoes-tabs.nav-tabs {
          border-bottom: 2px solid #dfe3e8;
          background-color: #f4f6f9;
        }
        .rescisoes-tabs.nav-tabs .nav-link {
          border: none;
          color: #6c757d;
          padding: 12px 24px;
          font-weight: 500;
          font-size: 14px;
          letter-spacing: 0.3px;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
          background: transparent;
          transition: color 0.2s ease, border-color 0.2s ease;
        }
        .rescisoes-tabs.nav-tabs .nav-link:hover {
          color: #343a40;
          border-bottom-color: #c7cdd4;
        }
        .rescisoes-tabs.nav-tabs .nav-link.active {
          color: #007bff;
          background: transparent;
          border-bottom-color: #007bff;
          font-weight: 600;
        }
        .rescisoes-tabs.nav-tabs .nav-link:focus {
          outline: none;
          box-shadow: none;
        }
      `}</style>
    </div>
  );
};

export default RelatorioRescisoes;
